import React, { useState, useEffect } from 'react';
import { BarChart3, PieChart, TrendingUp, Activity, Users, Target, Zap, Database, RefreshCw, Award, Trophy, Medal, Star, Flame, Users2, TrendingDown } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, Cell,
  ScatterChart, Scatter, AreaChart, Area, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';

export default function BiharAnalytics() {
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState('sample');
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  useEffect(() => {
    // Load sample dataset immediately
    loadAthletes('sample');
    // Load datasets list in background
    loadDatasets();
  }, []);

  useEffect(() => {
    if (selectedDataset && selectedDataset !== 'sample') {
      loadAthletes(selectedDataset);
    }
  }, [selectedDataset]);

  useEffect(() => {
    if (athletes.length > 0) {
      calculateAnalytics();
    }
  }, [athletes]);

  const loadDatasets = async () => {
    try {
      const response = await fetch('/api/bihar-datasets');
      const data = await response.json();
      const datasets = data.datasets || [];
      setDatasets(datasets);
      // Don't change selected dataset - keep sample as default
    } catch (error) {
      console.error('Error loading datasets:', error);
    }
  };

  const loadAthletes = async (datasetId) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/bihar-data?dataset_id=${datasetId}`);
      const data = await response.json();
      setAthletes(data.athletes || []);
    } catch (error) {
      console.error('Error loading athletes:', error);
    } finally {
      setLoading(false);
    }
  };

  // National and Olympic Standards
  const STANDARDS = {
    // Youth standards (14-16 years) - National level
    national: {
      height_cm: { male: 165, female: 155 },
      weight_kg: { male: 55, female: 48 },
      vertical_jump_cm: { male: 45, female: 35 },
      broad_jump_cm: { male: 200, female: 170 },
      sprint_30m_sec: { male: 5.0, female: 5.5 }, // lower is better
      run_800m_sec: { male: 150, female: 180 }, // lower is better (2:30 and 3:00)
      shuttle_run_sec: { male: 15, female: 17 }, // lower is better
      football_throw_m: { male: 25, female: 18 }
    },
    // Olympic/Elite standards - aspirational
    olympic: {
      height_cm: { male: 175, female: 165 },
      weight_kg: { male: 65, female: 55 },
      vertical_jump_cm: { male: 60, female: 50 },
      broad_jump_cm: { male: 250, female: 220 },
      sprint_30m_sec: { male: 4.2, female: 4.8 },
      run_800m_sec: { male: 120, female: 150 }, // 2:00 and 2:30
      shuttle_run_sec: { male: 12, female: 14 },
      football_throw_m: { male: 35, female: 25 }
    }
  };

  const calculateSportProbables = (athletes) => {
    const sportScores = {
      football: [],
      kabaddi: [],
      athletics: [],
      cycling: [],
      volleyball: []
    };

    athletes.forEach(athlete => {
      const height = athlete.height_cm || 0;
      const weight = athlete.weight_kg || 0;
      const vJump = athlete.vertical_jump_cm || 0;
      const bJump = athlete.broad_jump_cm || 0;
      const sprint = athlete.sprint_30m_sec || 999;
      const endurance = athlete.run_800m_sec || 999;
      const shuttle = athlete.shuttle_run_sec || 999;
      const isFemale = (athlete.gender_normalized || '').toLowerCase() === 'female';

      // Gender-aware thresholds (female thresholds are adjusted)
      const t = {
        sprintElite: isFemale ? 6.5 : 6,
        sprintGood: isFemale ? 7.5 : 7,
        bJumpHigh: isFemale ? 160 : 180,
        bJumpMid: isFemale ? 140 : 150,
        bJumpLow: isFemale ? 130 : 160,
        shuttleFast: isFemale ? 17 : 16,
        shuttleMid: isFemale ? 19 : 17,
        vJumpHigh: isFemale ? 30 : 35,
        vJumpMid: isFemale ? 22 : 25,
        vJumpElite: isFemale ? 25 : 30,
        heightTall: isFemale ? 160 : 170,
        heightMid: isFemale ? 155 : 165,
        heightMin: isFemale ? 150 : 160,
        weightMin: isFemale ? 42 : 50,
        enduranceElite: isFemale ? 270 : 240,
        enduranceGood: isFemale ? 330 : 300,
        weightLow: isFemale ? 40 : 45,
        weightHigh: isFemale ? 60 : 70
      };

      // Football scoring
      let footballScore = 0;
      if (sprint < t.sprintElite) footballScore += 30;
      else if (sprint < t.sprintGood) footballScore += 20;
      if (bJump > t.bJumpHigh) footballScore += 25;
      if (shuttle < t.shuttleFast) footballScore += 25;
      if (vJump > t.vJumpMid) footballScore += 20;
      if (footballScore >= 50) {
        sportScores.football.push({ name: athlete.full_name, score: footballScore, athlete });
      }

      // Kabaddi scoring
      let kabaddiScore = 0;
      if (weight > t.weightMin && height > t.heightMin) kabaddiScore += 25;
      if (shuttle < t.shuttleMid) kabaddiScore += 30;
      if (endurance < t.enduranceGood) kabaddiScore += 25;
      if (bJump > t.bJumpLow) kabaddiScore += 20;
      if (kabaddiScore >= 50) {
        sportScores.kabaddi.push({ name: athlete.full_name, score: kabaddiScore, athlete });
      }

      // Athletics scoring
      let athleticsScore = 0;
      if (sprint < t.sprintElite) athleticsScore += 35;
      else if (sprint < t.sprintGood) athleticsScore += 25;
      if (bJump > t.bJumpHigh) athleticsScore += 30;
      if (vJump > t.vJumpElite) athleticsScore += 25;
      if (athleticsScore >= 50) {
        sportScores.athletics.push({ name: athlete.full_name, score: athleticsScore, athlete });
      }

      // Cycling scoring
      let cyclingScore = 0;
      if (endurance < t.enduranceElite) cyclingScore += 35;
      else if (endurance < t.enduranceGood) cyclingScore += 25;
      if (bJump > t.bJumpMid) cyclingScore += 20;
      if (weight > t.weightLow && weight < t.weightHigh) cyclingScore += 20;
      if (height > t.heightMin) cyclingScore += 15;
      if (cyclingScore >= 50) {
        sportScores.cycling.push({ name: athlete.full_name, score: cyclingScore, athlete });
      }

      // Volleyball scoring
      let volleyballScore = 0;
      if (height > t.heightTall) volleyballScore += 35;
      else if (height > t.heightMid) volleyballScore += 25;
      if (vJump > t.vJumpHigh) volleyballScore += 30;
      else if (vJump > t.vJumpMid) volleyballScore += 20;
      if (shuttle < t.shuttleFast) volleyballScore += 20;
      if (volleyballScore >= 50) {
        sportScores.volleyball.push({ name: athlete.full_name, score: volleyballScore, athlete });
      }
    });

    // Sort by score and get top performers
    Object.keys(sportScores).forEach(sport => {
      sportScores[sport].sort((a, b) => b.score - a.score);
    });

    return sportScores;
  };

  const calculateAnalytics = () => {
    // Height distribution (with gender breakdown)
    const heightRanges = [
      { range: '< 140cm', min: 0, max: 140, count: 0, male: 0, female: 0 },
      { range: '140-150cm', min: 140, max: 150, count: 0, male: 0, female: 0 },
      { range: '150-160cm', min: 150, max: 160, count: 0, male: 0, female: 0 },
      { range: '160-170cm', min: 160, max: 170, count: 0, male: 0, female: 0 },
      { range: '> 170cm', min: 170, max: 300, count: 0, male: 0, female: 0 }
    ];

    // Weight distribution (with gender breakdown)
    const weightRanges = [
      { range: '< 30kg', min: 0, max: 30, count: 0, male: 0, female: 0 },
      { range: '30-40kg', min: 30, max: 40, count: 0, male: 0, female: 0 },
      { range: '40-50kg', min: 40, max: 50, count: 0, male: 0, female: 0 },
      { range: '50-60kg', min: 50, max: 60, count: 0, male: 0, female: 0 },
      { range: '> 60kg', min: 60, max: 200, count: 0, male: 0, female: 0 }
    ];

    // Performance categories
    const performanceData = {
      strength: [], // vertical jump, broad jump
      endurance: [], // 800m run
      speed: [], // 30m sprint
      power: [] // football throw
    };

    athletes.forEach(athlete => {
      const isMale = (athlete.gender_normalized || '').toLowerCase() === 'male';
      const isFemale = (athlete.gender_normalized || '').toLowerCase() === 'female';

      // Height distribution
      const height = athlete.height_cm;
      if (height) {
        heightRanges.forEach(range => {
          if (height >= range.min && height < range.max) {
            range.count++;
            if (isMale) range.male++;
            if (isFemale) range.female++;
          }
        });
      }

      // Weight distribution
      const weight = athlete.weight_kg;
      if (weight) {
        weightRanges.forEach(range => {
          if (weight >= range.min && weight < range.max) {
            range.count++;
            if (isMale) range.male++;
            if (isFemale) range.female++;
          }
        });
      }

      // Performance metrics
      if (athlete.vertical_jump_cm || athlete.broad_jump_cm) {
        performanceData.strength.push({
          name: athlete.full_name,
          vertical: athlete.vertical_jump_cm || 0,
          broad: athlete.broad_jump_cm || 0
        });
      }

      if (athlete.run_800m_sec) {
        performanceData.endurance.push({
          name: athlete.full_name,
          time: athlete.run_800m_sec
        });
      }

      if (athlete.sprint_30m_sec) {
        performanceData.speed.push({
          name: athlete.full_name,
          time: athlete.sprint_30m_sec
        });
      }

      if (athlete.football_throw_m) {
        performanceData.power.push({
          name: athlete.full_name,
          distance: athlete.football_throw_m
        });
      }
    });

    // Calculate averages
    const validHeights = athletes.filter(a => a.height_cm).map(a => a.height_cm);
    const validWeights = athletes.filter(a => a.weight_kg).map(a => a.weight_kg);
    const validVJumps = athletes.filter(a => a.vertical_jump_cm).map(a => a.vertical_jump_cm);
    const validBJumps = athletes.filter(a => a.broad_jump_cm).map(a => a.broad_jump_cm);
    const validSprints = athletes.filter(a => a.sprint_30m_sec).map(a => a.sprint_30m_sec);
    const valid800m = athletes.filter(a => a.run_800m_sec).map(a => a.run_800m_sec);

    const averages = {
      height: validHeights.length > 0 ? (validHeights.reduce((a, b) => a + b, 0) / validHeights.length).toFixed(1) : 0,
      weight: validWeights.length > 0 ? (validWeights.reduce((a, b) => a + b, 0) / validWeights.length).toFixed(1) : 0,
      verticalJump: validVJumps.length > 0 ? (validVJumps.reduce((a, b) => a + b, 0) / validVJumps.length).toFixed(1) : 0,
      broadJump: validBJumps.length > 0 ? (validBJumps.reduce((a, b) => a + b, 0) / validBJumps.length).toFixed(1) : 0,
      sprint30m: validSprints.length > 0 ? (validSprints.reduce((a, b) => a + b, 0) / validSprints.length).toFixed(2) : 0,
      run800m: valid800m.length > 0 ? (valid800m.reduce((a, b) => a + b, 0) / valid800m.length).toFixed(1) : 0
    };

    // Strength vs Endurance scatter (with gender for color-coding)
    const strengthEnduranceAll = athletes
      .filter(a => a.vertical_jump_cm && a.run_800m_sec)
      .map(a => ({
        name: a.full_name,
        strength: a.vertical_jump_cm,
        endurance: 600 - a.run_800m_sec, // Invert so higher is better
        height: a.height_cm,
        gender: a.gender_normalized || 'Unknown'
      }));
    const strengthEnduranceData = strengthEnduranceAll;
    const strengthEnduranceMale = strengthEnduranceAll.filter(d => d.gender === 'Male');
    const strengthEnduranceFemale = strengthEnduranceAll.filter(d => d.gender === 'Female');

    // Gender-specific averages for radar
    const maleAthletes = athletes.filter(a => (a.gender_normalized || '').toLowerCase() === 'male');
    const femaleAthletes = athletes.filter(a => (a.gender_normalized || '').toLowerCase() === 'female');

    const calcAvg = (arr, field) => {
      const valid = arr.filter(a => a[field]).map(a => a[field]);
      return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
    };

    const maleAvg = {
      height: calcAvg(maleAthletes, 'height_cm'),
      weight: calcAvg(maleAthletes, 'weight_kg'),
      vJump: calcAvg(maleAthletes, 'vertical_jump_cm'),
      bJump: calcAvg(maleAthletes, 'broad_jump_cm'),
      sprint: calcAvg(maleAthletes, 'sprint_30m_sec'),
      run800m: calcAvg(maleAthletes, 'run_800m_sec')
    };
    const femaleAvg = {
      height: calcAvg(femaleAthletes, 'height_cm'),
      weight: calcAvg(femaleAthletes, 'weight_kg'),
      vJump: calcAvg(femaleAthletes, 'vertical_jump_cm'),
      bJump: calcAvg(femaleAthletes, 'broad_jump_cm'),
      sprint: calcAvg(femaleAthletes, 'sprint_30m_sec'),
      run800m: calcAvg(femaleAthletes, 'run_800m_sec')
    };

    // Performance radar data (dual overlay: male vs female)
    const radarData = [
      { metric: 'Height', value: (parseFloat(averages.height) / 200) * 100, male: (maleAvg.height / 200) * 100, female: (femaleAvg.height / 200) * 100 },
      { metric: 'Weight', value: (parseFloat(averages.weight) / 80) * 100, male: (maleAvg.weight / 80) * 100, female: (femaleAvg.weight / 80) * 100 },
      { metric: 'V. Jump', value: (parseFloat(averages.verticalJump) / 100) * 100, male: (maleAvg.vJump / 100) * 100, female: (femaleAvg.vJump / 100) * 100 },
      { metric: 'B. Jump', value: (parseFloat(averages.broadJump) / 300) * 100, male: (maleAvg.bJump / 300) * 100, female: (femaleAvg.bJump / 300) * 100 },
      { metric: 'Sprint', value: Math.max(0, 100 - (parseFloat(averages.sprint30m) / 10) * 100), male: Math.max(0, 100 - (maleAvg.sprint / 10) * 100), female: Math.max(0, 100 - (femaleAvg.sprint / 10) * 100) },
      { metric: '800m', value: Math.max(0, 100 - (parseFloat(averages.run800m) / 400) * 100), male: Math.max(0, 100 - (maleAvg.run800m / 400) * 100), female: Math.max(0, 100 - (femaleAvg.run800m / 400) * 100) }
    ];

    // Calculate sport probables
    const sportProbables = calculateSportProbables(athletes);

    // Sport probables distribution for pie chart
    const sportDistribution = [
      { sport: 'Football', count: sportProbables.football.length, color: '#3b82f6' },
      { sport: 'Kabaddi', count: sportProbables.kabaddi.length, color: '#10b981' },
      { sport: 'Athletics', count: sportProbables.athletics.length, color: '#f59e0b' },
      { sport: 'Cycling', count: sportProbables.cycling.length, color: '#ef4444' },
      { sport: 'Volleyball', count: sportProbables.volleyball.length, color: '#8b5cf6' }
    ].filter(s => s.count > 0);

    // Standards comparison - calculate % of athletes meeting standards
    const standardsComparison = {
      national: {},
      olympic: {}
    };

    ['national', 'olympic'].forEach(level => {
      const standards = STANDARDS[level];
      Object.keys(standards).forEach(metric => {
        const metricKey = metric;
        const validAthletes = athletes.filter(a => a[metricKey] !== null && a[metricKey] !== undefined && !isNaN(a[metricKey]));
        
        if (validAthletes.length === 0) {
          standardsComparison[level][metric] = { percentage: 0, count: 0, total: 0 };
          return;
        }

        // Check if lower is better (time-based metrics)
        const lowerIsBetter = ['sprint_30m_sec', 'run_800m_sec', 'shuttle_run_sec'].includes(metric);

        // Use gender-specific standards: compare each athlete against their own gender's target
        const meetingStandard = validAthletes.filter(a => {
          const value = a[metricKey];
          const gender = (a.gender_normalized || '').toLowerCase();
          let targetValue;
          if (typeof standards[metric] === 'object') {
            targetValue = gender === 'female' ? standards[metric].female : standards[metric].male;
          } else {
            targetValue = standards[metric];
          }
          return lowerIsBetter ? value <= targetValue : value >= targetValue;
        });

        // Use average of male/female targets for display
        const displayTarget = typeof standards[metric] === 'object'
          ? (standards[metric].male + standards[metric].female) / 2
          : standards[metric];

        standardsComparison[level][metric] = {
          percentage: Math.round((meetingStandard.length / validAthletes.length) * 100),
          count: meetingStandard.length,
          total: validAthletes.length,
          average: validAthletes.reduce((sum, a) => sum + a[metricKey], 0) / validAthletes.length,
          target: displayTarget,
          targetMale: typeof standards[metric] === 'object' ? standards[metric].male : standards[metric],
          targetFemale: typeof standards[metric] === 'object' ? standards[metric].female : standards[metric]
        };
      });
    });

    // Elite performers (top 10% in any metric)
    const eliteAthletes = new Set();
    const metrics = ['height_cm', 'weight_kg', 'vertical_jump_cm', 'broad_jump_cm', 'sprint_30m_sec', 'run_800m_sec'];
    
    metrics.forEach(metric => {
      const validAthletes = athletes.filter(a => a[metric] !== null && a[metric] !== undefined && !isNaN(a[metric]));
      const sorted = [...validAthletes].sort((a, b) => {
        const lowerIsBetter = ['sprint_30m_sec', 'run_800m_sec', 'shuttle_run_sec'].includes(metric);
        return lowerIsBetter ? a[metric] - b[metric] : b[metric] - a[metric];
      });
      
      const top10Percent = sorted.slice(0, Math.ceil(sorted.length * 0.1));
      top10Percent.forEach(athlete => eliteAthletes.add(athlete.student_id || athlete.full_name));
    });

    // Performance trends - percentile distribution
    const percentileDistribution = {
      elite: 0,      // 90-100
      excellent: 0,  // 75-90
      good: 0,       // 50-75
      average: 0,    // 25-50
      developing: 0  // 0-25
    };

    athletes.forEach(athlete => {
      // Calculate average percentile across all metrics
      const percentiles = [
        athlete.height_cm_percentile,
        athlete.weight_kg_percentile,
        athlete.vertical_jump_cm_percentile,
        athlete.broad_jump_cm_percentile,
        athlete.sprint_30m_sec_percentile,
        athlete.run_800m_sec_percentile
      ].filter(p => p !== null && p !== undefined);

      if (percentiles.length > 0) {
        const avgPercentile = percentiles.reduce((a, b) => a + b, 0) / percentiles.length;
        
        if (avgPercentile >= 90) percentileDistribution.elite++;
        else if (avgPercentile >= 75) percentileDistribution.excellent++;
        else if (avgPercentile >= 50) percentileDistribution.good++;
        else if (avgPercentile >= 25) percentileDistribution.average++;
        else percentileDistribution.developing++;
      }
    });

    const performanceLevels = [
      { level: 'Elite (90-100%)', count: percentileDistribution.elite, color: '#10b981' },
      { level: 'Excellent (75-90%)', count: percentileDistribution.excellent, color: '#3b82f6' },
      { level: 'Good (50-75%)', count: percentileDistribution.good, color: '#f59e0b' },
      { level: 'Average (25-50%)', count: percentileDistribution.average, color: '#ef4444' },
      { level: 'Developing (0-25%)', count: percentileDistribution.developing, color: '#64748b' }
    ].filter(l => l.count > 0);

    // Gender comparison data for bar chart
    const genderComparison = [
      { metric: 'Height (cm)', male: parseFloat(maleAvg.height.toFixed(1)), female: parseFloat(femaleAvg.height.toFixed(1)) },
      { metric: 'Weight (kg)', male: parseFloat(maleAvg.weight.toFixed(1)), female: parseFloat(femaleAvg.weight.toFixed(1)) },
      { metric: 'V. Jump (cm)', male: parseFloat(maleAvg.vJump.toFixed(1)), female: parseFloat(femaleAvg.vJump.toFixed(1)) },
      { metric: 'B. Jump (cm)', male: parseFloat(maleAvg.bJump.toFixed(1)), female: parseFloat(femaleAvg.bJump.toFixed(1)) },
      { metric: 'Sprint (s)', male: parseFloat(maleAvg.sprint.toFixed(2)), female: parseFloat(femaleAvg.sprint.toFixed(2)) },
      { metric: '800m (s)', male: parseFloat(maleAvg.run800m.toFixed(1)), female: parseFloat(femaleAvg.run800m.toFixed(1)) }
    ];

    setAnalytics({
      heightRanges: heightRanges.filter(r => r.count > 0),
      weightRanges: weightRanges.filter(r => r.count > 0),
      performanceData,
      averages,
      strengthEnduranceData,
      strengthEnduranceMale,
      strengthEnduranceFemale,
      radarData,
      totalAthletes: athletes.length,
      maleCount: maleAthletes.length,
      femaleCount: femaleAthletes.length,
      sportProbables,
      sportDistribution,
      standardsComparison,
      eliteAthletesCount: eliteAthletes.size,
      performanceLevels,
      genderComparison
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-3" />
          <p className="text-slate-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-blue-600/20 rounded-lg">
              <BarChart3 className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Performance Analytics</h2>
              <p className="text-slate-400">Comprehensive visualization of athlete performance metrics</p>
            </div>
          </div>
          {datasets.length > 1 && (
            <select
              value={selectedDataset || ''}
              onChange={(e) => setSelectedDataset(e.target.value)}
              className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {datasets.map(dataset => (
                <option key={dataset.id} value={dataset.id}>
                  {dataset.name} ({dataset.record_count} athletes)
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {analytics && (
        <>
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-blue-500/30 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-8 h-8 text-blue-400" />
                <span className="text-3xl font-bold text-white">{analytics.totalAthletes}</span>
              </div>
              <p className="text-blue-300 text-sm font-medium">Total Athletes</p>
            </div>
            <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 border border-green-500/30 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-8 h-8 text-green-400" />
                <span className="text-3xl font-bold text-white">{analytics.averages.height}</span>
              </div>
              <p className="text-green-300 text-sm font-medium">Avg Height (cm)</p>
            </div>
            <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border border-purple-500/30 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <Target className="w-8 h-8 text-purple-400" />
                <span className="text-3xl font-bold text-white">{analytics.averages.verticalJump}</span>
              </div>
              <p className="text-purple-300 text-sm font-medium">Avg V. Jump (cm)</p>
            </div>
            <div className="bg-gradient-to-br from-orange-600/20 to-orange-800/20 border border-orange-500/30 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <Zap className="w-8 h-8 text-orange-400" />
                <span className="text-3xl font-bold text-white">{analytics.averages.sprint30m}</span>
              </div>
              <p className="text-orange-300 text-sm font-medium">Avg Sprint (sec)</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-800/20 border border-yellow-500/30 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <Trophy className="w-8 h-8 text-yellow-400" />
                <span className="text-3xl font-bold text-white">{analytics.eliteAthletesCount}</span>
              </div>
              <p className="text-yellow-300 text-sm font-medium">Elite Athletes</p>
            </div>
          </div>

          {/* Male vs Female Comparison */}
          {analytics.genderComparison && (analytics.maleCount > 0 || analytics.femaleCount > 0) && (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-indigo-600/20 rounded-lg">
                    <Users2 className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Male vs Female Comparison</h3>
                    <p className="text-slate-400 text-sm">Average metric values by gender</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <span className="flex items-center space-x-1"><span className="w-3 h-3 rounded-full bg-blue-500 inline-block"></span><span className="text-slate-300">{analytics.maleCount} Male</span></span>
                  <span className="flex items-center space-x-1"><span className="w-3 h-3 rounded-full bg-pink-500 inline-block"></span><span className="text-slate-300">{analytics.femaleCount} Female</span></span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.genderComparison}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="metric" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="male" fill="#3b82f6" name="Male Avg" />
                  <Bar dataKey="female" fill="#ec4899" name="Female Avg" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Standards Comparison Section */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-yellow-600/20 rounded-lg">
                <Award className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Standards Comparison</h3>
                <p className="text-slate-400 text-sm">Performance against National and Olympic benchmarks</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* National Standards */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Medal className="w-5 h-5 text-blue-400" />
                  <h4 className="text-lg font-semibold text-white">National Standards</h4>
                </div>
                <div className="space-y-4">
                  {Object.entries(analytics.standardsComparison.national).map(([metric, data]) => {
                    const metricNames = {
                      height_cm: 'Height',
                      weight_kg: 'Weight',
                      vertical_jump_cm: 'Vertical Jump',
                      broad_jump_cm: 'Broad Jump',
                      sprint_30m_sec: '30m Sprint',
                      run_800m_sec: '800m Run',
                      shuttle_run_sec: 'Shuttle Run',
                      football_throw_m: 'Football Throw'
                    };
                    return (
                      <div key={metric} className="bg-slate-700/30 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-slate-300">{metricNames[metric]}</span>
                          <span className="text-sm font-semibold text-blue-400">{data.percentage}%</span>
                        </div>
                        <div className="w-full bg-slate-600 rounded-full h-2.5">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-blue-400 h-2.5 rounded-full transition-all duration-500"
                            style={{ width: `${data.percentage}%` }}
                          />
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          {data.count} of {data.total} athletes meeting standard
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Olympic Standards */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  <h4 className="text-lg font-semibold text-white">Olympic Standards</h4>
                </div>
                <div className="space-y-4">
                  {Object.entries(analytics.standardsComparison.olympic).map(([metric, data]) => {
                    const metricNames = {
                      height_cm: 'Height',
                      weight_kg: 'Weight',
                      vertical_jump_cm: 'Vertical Jump',
                      broad_jump_cm: 'Broad Jump',
                      sprint_30m_sec: '30m Sprint',
                      run_800m_sec: '800m Run',
                      shuttle_run_sec: 'Shuttle Run',
                      football_throw_m: 'Football Throw'
                    };
                    return (
                      <div key={metric} className="bg-slate-700/30 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-slate-300">{metricNames[metric]}</span>
                          <span className="text-sm font-semibold text-yellow-400">{data.percentage}%</span>
                        </div>
                        <div className="w-full bg-slate-600 rounded-full h-2.5">
                          <div 
                            className="bg-gradient-to-r from-yellow-500 to-yellow-400 h-2.5 rounded-full transition-all duration-500"
                            style={{ width: `${data.percentage}%` }}
                          />
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          {data.count} of {data.total} athletes meeting standard
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Sport Probables Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sport Distribution Pie */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Users2 className="w-5 h-5 text-green-400" />
                <h3 className="text-lg font-semibold text-white">Sport Probables Distribution</h3>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Athletes showing strong potential for specific sports based on their performance metrics
              </p>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPie>
                  <Pie
                    data={analytics.sportDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ sport, count, percent }) => `${sport}: ${count} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {analytics.sportDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            </div>

            {/* Sport Probables Bar Chart */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Flame className="w-5 h-5 text-orange-400" />
                <h3 className="text-lg font-semibold text-white">Top Sport Probables</h3>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Number of athletes showing strong suitability for each sport category
              </p>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.sportDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="sport" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]}>
                    {analytics.sportDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Performance Levels Distribution */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Star className="w-5 h-5 text-purple-400" />
              <h3 className="text-lg font-semibold text-white">Performance Level Distribution</h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Athletes categorized by their average percentile performance across all metrics
            </p>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.performanceLevels} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                <XAxis type="number" stroke="#94a3b8" />
                <YAxis dataKey="level" type="category" stroke="#94a3b8" width={150} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                  {analytics.performanceLevels.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Height Distribution - Grouped Bar Chart by Gender */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-2 flex items-center space-x-2">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                <span>Height Distribution by Gender</span>
              </h3>
              <p className="text-sm text-slate-400 mb-4">Shows the spread of athlete heights across different ranges, split by gender. Helps identify height-dependent sport suitability.</p>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.heightRanges}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="range" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="male" fill="#3b82f6" name="Male" />
                  <Bar dataKey="female" fill="#ec4899" name="Female" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Weight Distribution - Grouped Bar Chart by Gender */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-2 flex items-center space-x-2">
                <BarChart3 className="w-5 h-5 text-green-400" />
                <span>Weight Distribution by Gender</span>
              </h3>
              <p className="text-sm text-slate-400 mb-4">Displays weight categories across the athlete population, split by gender. Useful for nutrition program planning.</p>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.weightRanges}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="range" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="male" fill="#3b82f6" name="Male" />
                  <Bar dataKey="female" fill="#ec4899" name="Female" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Strength vs Endurance Scatter */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-2 flex items-center space-x-2">
                <Activity className="w-5 h-5 text-purple-400" />
                <span>Strength vs Endurance</span>
              </h3>
              <p className="text-sm text-slate-400 mb-4">Reveals the relationship between explosive power (vertical jump) and cardiovascular endurance (800m). Athletes in the top-right are all-rounders with both strength and stamina.</p>
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="strength" name="Vertical Jump" stroke="#94a3b8" />
                  <YAxis dataKey="endurance" name="Endurance Score" stroke="#94a3b8" />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Legend />
                  {analytics.strengthEnduranceMale.length > 0 && (
                    <Scatter name="Male" data={analytics.strengthEnduranceMale} fill="#3b82f6" fillOpacity={0.6} />
                  )}
                  {analytics.strengthEnduranceFemale.length > 0 && (
                    <Scatter name="Female" data={analytics.strengthEnduranceFemale} fill="#ec4899" fillOpacity={0.6} />
                  )}
                </ScatterChart>
              </ResponsiveContainer>
            </div>

            {/* Performance Radar - Male vs Female */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-2 flex items-center space-x-2">
                <Target className="w-5 h-5 text-orange-400" />
                <span>Performance Profile: Male vs Female</span>
              </h3>
              <p className="text-sm text-slate-400 mb-4">Compares average performance between genders across all metrics. Larger areas indicate stronger performance categories.</p>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={analytics.radarData}>
                  <PolarGrid stroke="#475569" />
                  <PolarAngleAxis dataKey="metric" stroke="#94a3b8" />
                  <PolarRadiusAxis stroke="#94a3b8" />
                  <Radar name="Male Avg" dataKey="male" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                  <Radar name="Female Avg" dataKey="female" stroke="#ec4899" fill="#ec4899" fillOpacity={0.3} />
                  <Legend />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Strength Metrics Bar Chart */}
            {analytics.performanceData.strength.length > 0 && (
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center space-x-2">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                  <span>Top Strength Performers</span>
                </h3>
                <p className="text-sm text-slate-400 mb-4">Highlights athletes with exceptional jumping ability (vertical and broad jump). These individuals show promise for power-based sports like athletics, volleyball, and basketball.</p>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.performanceData.strength.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                    <XAxis dataKey="name" stroke="#94a3b8" angle={-45} textAnchor="end" height={100} />
                    <YAxis stroke="#94a3b8" />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="vertical" fill="#3b82f6" name="Vertical Jump (cm)" />
                    <Bar dataKey="broad" fill="#10b981" name="Broad Jump (cm)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Endurance Line Chart */}
            {analytics.performanceData.endurance.length > 0 && (
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  <span>800m Run Performance</span>
                </h3>
                <p className="text-sm text-slate-400 mb-4">Displays endurance capabilities across top athletes. Lower times indicate better cardiovascular fitness, ideal for cycling, long-distance running, and endurance sports.</p>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics.performanceData.endurance.slice(0, 15)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                    <XAxis dataKey="name" stroke="#94a3b8" angle={-45} textAnchor="end" height={100} />
                    <YAxis stroke="#94a3b8" />
                    <Tooltip />
                    <Area type="monotone" dataKey="time" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Time (sec)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Performance Summary Table */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <Database className="w-5 h-5 text-slate-400" />
              <span>Performance Averages Summary</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-400">{analytics.averages.height}</p>
                <p className="text-xs text-slate-400 mt-1">Avg Height (cm)</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-400">{analytics.averages.weight}</p>
                <p className="text-xs text-slate-400 mt-1">Avg Weight (kg)</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-purple-400">{analytics.averages.verticalJump}</p>
                <p className="text-xs text-slate-400 mt-1">Avg V. Jump (cm)</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-orange-400">{analytics.averages.broadJump}</p>
                <p className="text-xs text-slate-400 mt-1">Avg B. Jump (cm)</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-yellow-400">{analytics.averages.sprint30m}</p>
                <p className="text-xs text-slate-400 mt-1">Avg Sprint (s)</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-400">
                  {Math.floor(analytics.averages.run800m / 60)}:{(analytics.averages.run800m % 60).toFixed(0).padStart(2, '0')}
                </p>
                <p className="text-xs text-slate-400 mt-1">Avg 800m (min:sec)</p>
              </div>
            </div>
          </div>
        </>
      )}

      {!analytics && !loading && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-12 text-center">
          <BarChart3 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">Select a dataset to view analytics</p>
        </div>
      )}
    </div>
  );
}
